<script>
/**
 * 상품 관리 목록 페이지.
 *
 * 기능:
 * - 상품 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 키워드(상품명) / 카테고리 / 노출여부
 * - view_yn 토글: 목록에서 v-switch로 즉시 ON/OFF
 * - 등록 버튼 → ProductCreateDialog 오픈
 * - 행 클릭 → /products/:productCode 상세 페이지 이동
 */
import { getProductList, toggleProductView } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getAdminInfo } from '@/utils/token.js'
import ProductThumbnail from './ProductThumbnail.vue'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'ProductListPage',

  components: { ProductThumbnail, AppPagination },

  data() {
    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        categoryCode: '',
        viewYn: '',
      },

      /** 카테고리 필터 옵션 (API에서 로딩) */
      categoryOptions: [{ title: '전체', value: '' }],

      /** 노출여부 select 옵션 */
      viewYnOptions: [
        { title: '전체', value: '' },
        { title: '노출', value: 'Y' },
        { title: '미노출', value: 'N' },
      ],

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
      createDialog: false,

      /** view_yn 토글 중인 상품 코드 Set (중복 클릭 방지) */
      togglingSet: new Set(),

      /** 로그인한 관리자 정보 */
      adminInfo: {},

    }
  },

  computed: {
    /** HQ 관리자 여부: 등록/토글 버튼 노출 제어 */
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    // 카테고리 목록과 상품 목록을 병렬로 로딩
    Promise.all([this.fetchCategories(), this.fetchList()])
  },

  methods: {
    /**
     * 카테고리 전체 목록을 로딩한다 (검색 필터용).
     * 페이지당 100건으로 설정하여 대부분의 경우 전체를 한 번에 가져온다.
     */
    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = [...(res.data.data?.items || [])].sort((a, b) => {
            const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
            const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
            if (ao !== bo) return ao - bo
            return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
          })
          this.categoryOptions = [
            { title: '전체', value: '' },
            ...list.map(c => ({ title: c.categoryName, value: c.categoryCode })),
          ]
        }
      } catch (e) {
        console.error('[ProductListPage] fetchCategories error:', e)
      }
    },

    /**
     * 서버에서 상품 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getProductList({
          keyword:      this.search.keyword      || undefined,
          categoryCode: this.search.categoryCode || undefined,
          viewYn:       this.search.viewYn       || undefined,
          page:         this.page,
          size:         this.size,
        })
        if (res.data?.code === 0) {
          const rows = res.data.data?.items || []
          this.items = [...rows].sort((a, b) => {
            const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
            const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
            if (ao !== bo) return ao - bo
            return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
          })
          this.total = res.data.data?.total || 0
        }
      } catch (e) {
        console.error('[ProductListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼 클릭: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      this.search = { keyword: '', categoryCode: '', viewYn: '' }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 상품 상세 페이지로 이동 */
    goToDetail(item) {
      this.$router.push(`/products/${item.productCode}`)
    },

    /** 상품 등록 페이지로 이동 */
    goToCreate() {
      this.$router.push('/products/create')
    },

    /**
     * view_yn 토글 클릭.
     * 중복 클릭을 방지하기 위해 togglingSet으로 처리 중 상태를 관리한다.
     *
     * @param {Object} item - 토글할 상품 항목
     * @param {Event} event - 이벤트 버블링 차단용
     */
    async handleToggleView(item) {
      if (!this.isHq) return
      if (this.togglingSet.has(item.productCode)) return

      this.togglingSet = new Set([...this.togglingSet, item.productCode])
      try {
        const res = await toggleProductView(item.productCode)
        if (res.data?.code === 0) {
          // 서버 응답으로 목록 갱신 (토글 반영 보장)
          const idx = this.items.findIndex(i => i.productCode === item.productCode)
          if (idx >= 0) {
            const newViewYn = res.data.viewYn
            this.items.splice(idx, 1, { ...this.items[idx], viewYn: newViewYn })
          }
        }
      } catch (e) {
        console.error('[ProductListPage] toggleView error:', e)
      } finally {
        const next = new Set(this.togglingSet)
        next.delete(item.productCode)
        this.togglingSet = next
      }
    },

    /** 날짜 포맷: YYYY-MM-DD */
    formatDate(dt) {
      return dt ? dt.substring(0, 10) : '-'
    },

    /** 통화 표시 */
    formatCurrency(val) {
      return val != null && String(val).trim() !== '' ? val : '-'
    },

    /** 임가공비 (숫자 천 단위) */
    formatConventionCost(val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      return Number.isNaN(n) ? '-' : n.toLocaleString('ko-KR')
    },

    /** 중량 (소수 둘째 자리까지) */
    formatWeight(val) {
      if (val === null || val === undefined || val === '') return '-'
      const n = Number(val)
      return Number.isNaN(n)
        ? '-'
        : n.toLocaleString('ko-KR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">상품 관리</div>
        <div class="text-body-2 text-grey mt-1">상품 조회 및 등록</div>
      </div>
      <!-- 등록 버튼 (HQ 전용) -->
      <v-btn
        v-if="isHq"
        color="primary"
        prepend-icon="mdi-plus"
        @click="goToCreate"
      >
        상품 등록
      </v-btn>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <!-- 키워드 검색 -->
          <v-col cols="12" sm="4">
            <v-text-field
              v-model="search.keyword"
              label="상품 검색"
              placeholder="상품명"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
          <!-- 카테고리 필터 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.categoryCode"
              :items="categoryOptions"
              item-title="title"
              item-value="value"
              label="카테고리"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 노출여부 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.viewYn"
              :items="viewYnOptions"
              item-title="title"
              item-value="value"
              label="노출여부"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          상품 목록
          <v-chip size="small" color="primary" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 -->
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <v-table density="compact" class="border-t">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">썸네일</th>
            <th class="text-center">상품코드</th>
            <th class="text-center">상품명</th>
            <th class="text-center">카테고리</th>
            <th class="text-center">통화</th>
            <th class="text-end">임가공비</th>
            <th class="text-end">중량</th>
            <th class="text-center">정렬순서</th>
            <th class="w-1/12 text-center">노출여부</th>
            <th class="text-center">등록일</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.productCode"
            style="cursor: pointer;"
            class="product-row"
            @click="goToDetail(item)"
          >
            <!-- 번호 -->
            <td class="text-center text-caption text-grey text-center">
              {{ total - (page - 1) * size - index }}
            </td>
            <!-- 썸네일 -->
            <td class="py-2 justify-items-center flex justify-center">
              <ProductThumbnail :object-key="item.thumbnailImageUrl" :size="120" />
            </td>
            <!-- 상품코드 -->
            <td class="text-center text-caption text-grey-darken-1">{{ item.productCode }}</td>
            <!-- 상품명 -->
            <td class="text-center font-weight-medium">{{ item.productName || '-' }}</td>
            <!-- 카테고리 -->
            <td class="text-center">
              <v-chip
                v-if="item.categoryName"
                size="x-small"
                color="primary"
                variant="tonal"
                label
              >
                {{ item.categoryName }}
              </v-chip>
              <span v-else class="text-center text-caption text-grey">-</span>
            </td>
            <!-- 통화 -->
            <td class="text-caption text-center">{{ formatCurrency(item.currency) }}</td>
            <!-- 임가공비 -->
            <td class="text-caption text-end">{{ formatConventionCost(item.conventionCost) }}</td>
            <!-- 중량 -->
            <td class="text-caption text-end">{{ formatWeight(item.weight) }}</td>
            <td class="text-caption text-center">{{ item.orderby ?? '-' }}</td>
            <!-- 노출여부 토글 -->
            <td @click.stop class="justify-items-center">
              <div @click.stop="handleToggleView(item)">
                <v-switch
                  :model-value="item.viewYn === 'Y'"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!isHq || togglingSet.has(item.productCode)"
                  :loading="togglingSet.has(item.productCode)"
                />
              </div>
            </td>
            <!-- 등록일 -->
            <td class="text-center text-caption text-center">{{ formatDate(item.insertDt) }}</td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!loading && items.length === 0">
            <td colspan="11" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-cube-off-outline
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>

  </div>
</template>

<style scoped>
.product-row:hover {
  background-color: rgba(205, 178, 122, 0.06);
}
</style>
